import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { BarChart3, MessageSquare, FolderKanban, FileText, LogOut, TrendingUp, Users, Eye, Briefcase, Settings, Globe } from 'lucide-react';
import '@/styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allVisitors, setAllVisitors] = useState([]);
  const [showAllVisitors, setShowAllVisitors] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    loadAnalytics();
  }, [navigate]);

  const loadAnalytics = async () => {
    try {
      const [data, visitors] = await Promise.all([
        adminApi.getAnalyticsSummary(),
        adminApi.getAllVisitors()
      ]);
      setAnalytics(data);
      setAllVisitors(visitors);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminApi.logout();
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>AK Admin</h2>
        </div>
        <nav className="sidebar-nav">
          <button className="nav-item active" onClick={() => navigate('/admin/dashboard')}>
            <BarChart3 size={20} />
            Dashboard
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/contacts')}>
            <MessageSquare size={20} />
            Messages
            {analytics?.newContacts > 0 && (
              <span className="badge">{analytics.newContacts}</span>
            )}
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/projects')}>
            <FolderKanban size={20} />
            Projects
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/articles')}>
            <FileText size={20} />
            Articles
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/experience')}>
            <Briefcase size={20} />
            Experience
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/settings')}>
            <Settings size={20} />
            Settings
          </button>
        </nav>
        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={20} />
          Logout
        </button>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <h1>Dashboard Overview</h1>
          <p>Welcome back, {adminApi.getUser()?.username}!</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Eye size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{analytics?.totalViews || 0}</div>
              <div className="stat-label">Total Views</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{analytics?.uniqueVisitors || 0}</div>
              <div className="stat-label">Unique Visitors</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <MessageSquare size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{analytics?.totalContacts || 0}</div>
              <div className="stat-label">Total Messages</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{analytics?.newContacts || 0}</div>
              <div className="stat-label">New Messages</div>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Top Pages</h3>
            <div className="top-pages">
              {analytics?.topPages?.map((page, idx) => (
                <div key={idx} className="page-item">
                  <span className="page-name">{page.page}</span>
                  <span className="page-count">{page.count} views</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header-row">
              <h3><Globe size={18} /> Visitor IPs</h3>
              <button className="toggle-btn" onClick={() => setShowAllVisitors(!showAllVisitors)}>
                {showAllVisitors ? 'Show Recent' : 'Show All'}
              </button>
            </div>
            <div className="visitors-list">
              {(showAllVisitors ? allVisitors : analytics?.recentVisitors || []).slice(0, showAllVisitors ? 50 : 5).map((visitor, idx) => (
                <div key={idx} className="visitor-item" data-testid={`visitor-${idx}`}>
                  <div className="visitor-info">
                    <div className="visitor-ip">
                      {visitor.ip_address || 'Unknown IP'}
                      {visitor.country && visitor.country !== 'Unknown' && <span className="country-badge">{visitor.country}</span>}
                    </div>
                    <div className="visitor-page">{visitor.page_view || (visitor.pages ? visitor.pages.join(', ') : '')}</div>
                  </div>
                  <div className="visitor-meta">
                    <span className="visitor-time">{new Date(visitor.timestamp || visitor.last_visit).toLocaleString()}</span>
                    {visitor.visit_count && <span className="visit-count">{visitor.visit_count} visits</span>}
                  </div>
                </div>
              ))}
              {(showAllVisitors ? allVisitors : analytics?.recentVisitors || []).length === 0 && (
                <div className="no-data">No visitors recorded yet</div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Recent Messages</h3>
            <div className="recent-contacts">
              {analytics?.recentContacts?.slice(0, 5).map((contact) => (
                <div key={contact.id} className="contact-item">
                  <div className="contact-info">
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-subject">{contact.subject}</div>
                  </div>
                  {contact.status === 'new' && <span className="new-badge">New</span>}
                </div>
              ))}
              <button className="view-all-btn" onClick={() => navigate('/admin/contacts')}>
                View All Messages
              </button>
            </div>
          </div>
        </div>

        <div className="quick-stats">
          <div className="quick-stat">
            <span className="quick-stat-label">Projects</span>
            <span className="quick-stat-value">{analytics?.totalProjects || 0}</span>
          </div>
          <div className="quick-stat">
            <span className="quick-stat-label">Articles</span>
            <span className="quick-stat-value">{analytics?.totalArticles || 0}</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;